import { Ros, Service } from "roslib";
import { ServiceCaller } from "../base";

export type RosLibJsServiceOptions = {
  ros: Ros;
};

export class RosLibJsServiceCaller extends ServiceCaller {
  private ros: Ros;
  private serviceTypeCache: Map<string, string>;

  constructor(options: RosLibJsServiceOptions) {
    super();
    this.ros = options.ros;
    this.serviceTypeCache = new Map();
  }

  private getServiceType = async (serviceName: string): Promise<string> => {
    // Check cache first
    if (this.serviceTypeCache.has(serviceName)) {
      return this.serviceTypeCache.get(serviceName)!;
    }

    return new Promise((resolve, reject) => {
      this.ros.getServiceType(
        serviceName,
        (type: string) => {
          this.serviceTypeCache.set(serviceName, type);
          resolve(type);
        },
        (error: string) => reject(new Error(error))
      );
    });
  };

  callService = async <ReqType, ResType>(
    serviceName: string,
    data: ReqType,
    options?: { serviceType?: string; timeoutMs?: number }
  ): Promise<ResType> => {
    // Use provided service type or discover it from ROS
    const serviceType = options?.serviceType ?? await this.getServiceType(serviceName);
    const timeoutMs = options?.timeoutMs;

    return new Promise((resolve, reject) => {
      const service = new Service<ReqType, ResType>({
        ros: this.ros,
        name: serviceName,
        serviceType: serviceType,
      });

      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        fn();
      };

      if (timeoutMs != null && timeoutMs > 0 && Number.isFinite(timeoutMs)) {
        timer = setTimeout(() => {
          settle(() => reject(new Error(
            `Service call to "${serviceName}" timed out after ${timeoutMs}ms`
          )));
        }, timeoutMs);
      }

      service.callService(
        data,
        (response: ResType) => {
          settle(() => resolve(response));
        },
        (error: string) => {
          settle(() => reject(new Error(error)));
        }
      );
    });
  };
}
